import styled from "styled-components";
import useBranchInfo from "features/owner/hooks/useBranchInfo";
import Refresh from "shared/components/Refresh";
import { hw } from "features/homework/homeworkUi";

interface BranchInfoProps{
    branch_id:string
}

const BranchInfo = ({branch_id}:BranchInfoProps) => {
    const {branch, studentCount, teacherCount, onRefreshClick} = useBranchInfo(branch_id);

    if (!branch_id) {
        return (
            <Container>
                <Header>
                    <div>
                        <Title>지점정보</Title>
                        <Sub>오른쪽 목록에서 지점을 선택하면 상세 정보가 표시됩니다.</Sub>
                    </div>
                </Header>
                <Empty>
                    <EmptyTitle>지점을 선택하세요</EmptyTitle>
                    <EmptyText>목록에서 지점명을 클릭하면 계정 정보를 확인할 수 있습니다.</EmptyText>
                </Empty>
            </Container>
        );
    }

    const permission = [
        branch.report_permission?.word ? "단어" : null,
        branch.report_permission?.sentence ? "문장" : null,
        branch.report_permission?.video ? "영상" : null,
    ].filter(Boolean).join(" · ");

    const rows = [
        { label: "상호명", value: branch.name },
        { label: "원장 성명", value: branch.owner_name },
        { label: "아이디", value: branch.id },
        { label: "비밀번호", value: branch.pw },
        { label: "학원 전화번호", value: branch.tel },
        { label: "원장 전화번호", value: branch.owner_tel },
        { label: "원생수", value: `${studentCount}명` },
        { label: "교사수", value: `${teacherCount}명` },
        { label: "과제 이용 권한", value: permission },
        { label: "주소", value: [branch.address, branch.address_detail].filter(Boolean).join(" "), wide: true },
    ];

    return (
        <Container>
            <Header>
                <div>
                    <Title>지점정보</Title>
                    <Sub>{branch.name || "선택한 지점"}의 계정 정보입니다.</Sub>
                </div>
                <Refresh onRefreshClick={onRefreshClick}/>
            </Header>
            <Grid>
                {rows.map((row) => (
                    <Field key={row.label} $wide={row.wide}>
                        <Label>{row.label}</Label>
                        <Value>{row.value || "-"}</Value>
                    </Field>
                ))}
            </Grid>
        </Container>
    )
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-width: 0;
    min-height: 0;
    height: 100%;
    padding: 20px 22px;
    background: ${hw.surface};
    border: 1px solid ${hw.border};
    border-radius: 16px;
    box-shadow: ${hw.shadow};
`

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 12px;
    padding-bottom: 14px;
    border-bottom: 1px solid ${hw.border};
    flex-shrink: 0;
`

const Title = styled.h2`
    margin: 0;
    color: ${hw.text};
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.02em;
`

const Sub = styled.p`
    margin: 4px 0 0;
    font-size: 13px;
    color: ${hw.textMuted};
`

const Grid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    overflow: auto;
    min-height: 0;
    align-content: start;

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
    }
`

const Field = styled.div<{ $wide?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 14px 16px;
    background: ${hw.bg};
    border: 1px solid ${hw.border};
    border-radius: 12px;
    min-width: 0;
    grid-column: ${props => props.$wide ? "1 / -1" : "auto"};
`

const Label = styled.div`
    font-size: 12px;
    font-weight: 600;
    color: ${hw.textMuted};
`

const Value = styled.div`
    font-size: 15px;
    font-weight: 600;
    color: ${hw.text};
    word-break: break-all;
`

const Empty = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    text-align: center;
    min-height: 240px;
`

const EmptyTitle = styled.div`
    font-size: 16px;
    font-weight: 700;
    color: ${hw.text};
`

const EmptyText = styled.div`
    font-size: 13px;
    color: ${hw.textMuted};
    line-height: 1.5;
`

export default BranchInfo;
